import { createDraftSafeSelector } from "@reduxjs/toolkit";

import {
  ContinueQuotePolicyFragmentFragment,
  GeoCodeAddressDocument,
  GeoCodeAddressQuery,
  GeoCodeInput,
  GeoCodeResult,
  PreciselyAddress,
} from "@covertree/new-business-graphql";
import { CountyBlockerErrorType, RedZoneErrorType } from "@covertree/types";

import { createAsyncThunkSlice, typedReducerCreator } from "../../utils";
import { setUnitsDetails } from "../unitsConstructionAndDetails";

interface PropertyAddress {
  propertyAddress?: PreciselyAddress;
  lotUnit?: string;
  geoCode: {
    loading: boolean;
    error?: Error;
    result?: GeoCodeResult;
  };
  hasSeenSurplusLinesModal: boolean;
}

const initialState: PropertyAddress = {
  propertyAddress: undefined,
  lotUnit: undefined,
  geoCode: {
    loading: false,
  },
  hasSeenSurplusLinesModal: false,
};

export const homeAddressSlice = createAsyncThunkSlice({
  name: "homeAddress",
  initialState,
  selectors: {
    selectNewHomeAddress: (state) => state,
    selectNewHomeState: (state) => state.propertyAddress?.state,
    selectGeoCodeResult: (state) => state.geoCode.result,
    selectGeoCodeLoading: (state) => state.geoCode.loading,
    selectPropertyAddress: (state) => state.propertyAddress,
    selectLotUnit: (state) => state.lotUnit,
    selectHasSeenSurplusLinesModal: (state) => state.hasSeenSurplusLinesModal,
  },
  reducers: (c) => {
    const { create, createWithType } = typedReducerCreator(c);

    return {
      /**
       * Grabs GeoCode data from the API and puts into the state
       */
      getGeoCode: createWithType<GeoCodeAddressQuery, GeoCodeInput>(
        async (address, thunkAPI) => {
          try {
            const { data, error } =
              await thunkAPI.extra.client.query<GeoCodeAddressQuery>({
                query: GeoCodeAddressDocument,
                variables: {
                  address,
                },
              });

            if (error) {
              return thunkAPI.rejectWithValue(error);
            }

            if (data.geoCodeAddress?.homeLocation) {
              thunkAPI.dispatch(
                setUnitsDetails({
                  unitIndex: 0,
                  unitDetails: {
                    location: data.geoCodeAddress.homeLocation,
                    communityName: data.geoCodeAddress.communityName,
                  },
                })
              );
            }

            return data;
          } catch (e) {
            // eslint-disable-next-line
            // @ts-ignore
            if (e?.graphQLErrors) {
              // eslint-disable-next-line
              // @ts-ignore
              const errors = e.graphQLErrors as {
                errorType: string;
                message: string;
              }[];

              const fastDeclineError = errors.find(({ errorType }) =>
                Object.values({
                  ...RedZoneErrorType,
                  ...CountyBlockerErrorType,
                }).includes(
                  errorType as RedZoneErrorType | CountyBlockerErrorType
                )
              );

              if (fastDeclineError) {
                return thunkAPI.rejectWithValue({
                  errorType: fastDeclineError.errorType,
                  message: fastDeclineError.message,
                });
              }
            }
            const message = e instanceof Error ? e.message : "unexpected error";
            return thunkAPI.rejectWithValue({ message });
          }
        },
        {
          pending: (state) => {
            state.geoCode.loading = true;
          },
          fulfilled: (state, { payload }) => {
            state.geoCode.error = undefined;
            state.geoCode.result = payload.geoCodeAddress ?? undefined;
          },
          rejected: (state, { payload }) => {
            state.geoCode.error = payload as Error;
            state.geoCode.result = undefined;
          },
          settled: (state) => {
            state.geoCode.loading = false;
          },
        }
      ),
      /**
       * Sets the property address in state
       */
      setPropertyAddress: create.reducer<{
        address: PreciselyAddress;
        lotUnit?: string;
      }>((state, { payload }) => {
        state.propertyAddress = payload.address;
        state.lotUnit = payload.lotUnit;
      }),
      /**
       * Sets the GeoCode result in state
       * @deprecated - Probably you can just use getGeoCode on D2C and that one won't be used anymore
       */
      setGeoCode: create.reducer<GeoCodeResult>((state, { payload }) => {
        state.geoCode.loading = false;
        state.geoCode.error = undefined;
        state.geoCode.result = payload;
      }),
      /**
       * Set Home Address details based on Policy details in CYQ
       */
      setHomeAddressBasedOnPolicyDetails: create.reducer<
        Pick<ContinueQuotePolicyFragmentFragment, "units">
      >((state, { payload }) => {
        const { address, details } = payload.units[0];

        state.propertyAddress = {
          addressLastLine: `${address.city}, ${address.state} ${address.zipCode}`,
          city: address.city,
          country: address.countryCode,
          formattedAddress: details.formattedLocationAddress || "",
          mainAddressLine: address.streetAddress,
          postCode: address.zipCode,
          state: address.state,
          streetName: address.streetAddress,
        };
        if (address.unitNumber) state.lotUnit = address.unitNumber;
        state.geoCode.result = {
          latitude: +details.latitude!,
          longitude: +details.longitude!,
          homeLocation: details.unitLocation,
          ...(details.communityName
            ? { communityName: details.communityName }
            : {}),
          formattedLocationAddress: details.formattedLocationAddress || "",
          state: address.state,
          county: address.county || "",
        };
      }),
      /**
       * Marks surplus lines modal as seen
       */
      setHasSeenSurplusLinesModal: create.reducer<boolean>(
        (state, { payload }) => {
          state.hasSeenSurplusLinesModal = payload;
        }
      ),
      /**
       * Resets state the initial value
       */
      resetHomeAddress: create.reducer(() => initialState),
    };
  },
});

export const {
  actions: {
    getGeoCode,
    setPropertyAddress,
    setGeoCode,
    resetHomeAddress,
    setHomeAddressBasedOnPolicyDetails,
    setHasSeenSurplusLinesModal,
  },
  selectors: {
    selectNewHomeAddress,
    selectNewHomeState,
    selectPropertyAddress,
    selectLotUnit,
    selectGeoCodeResult,
    selectGeoCodeLoading,
    selectHasSeenSurplusLinesModal,
  },
} = homeAddressSlice;

/**
 * Draft Selector, which grabs Property Address state needed to API call
 * Can be used within reducers of other slices
 */
export const getPropertyAddressDetails = createDraftSafeSelector(
  [selectPropertyAddress, selectLotUnit],
  (propertyAddress, lotUnit) =>
    propertyAddress ? { ...propertyAddress, lotUnit } : undefined
);
